"use client";

import { CROWN_WHEEL, WheelOption } from "@/data/wheels";
import { CrownStage } from "@/hooks/useGameState";
import SpinningWheel from "@/components/SpinningWheel";

interface CrownOverlayProps {
  stage: CrownStage;
  spinToken: number;
  pendingOption: WheelOption | null;
  bonusOption: WheelOption | null;
  onTakeCrown: () => void;
  onSpin: () => void;
  onSpinEnd: () => void;
  onContinue: () => void;
  onTick: (strength: number) => void;
}

export default function CrownOverlay({
  stage,
  spinToken,
  pendingOption,
  bonusOption,
  onTakeCrown,
  onSpin,
  onSpinEnd,
  onContinue,
  onTick,
}: CrownOverlayProps) {
  if (!stage) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-black/85 px-4 backdrop-blur-sm">
      {stage === "choice" && (
        <div className="animate-drop-in flex flex-col items-center gap-6 text-center">
          <div className="text-[clamp(3rem,12vw,7rem)] leading-none">👑</div>
          <h2 className="font-black uppercase tracking-[0.2em] text-yellow-400 text-[clamp(1.5rem,5vw,3rem)] [text-shadow:0_0_30px_rgba(250,204,21,0.6)]">
            You broke the scale.
          </h2>
          <p className="text-[clamp(1rem,2.5vw,1.5rem)] font-semibold text-white/80">
            Double or nothing?
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={onTakeCrown}
              className="rounded-full border-2 border-yellow-400 px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-yellow-300 transition hover:bg-yellow-400 hover:text-black"
            >
              Take the Crown
            </button>
            <button
              onClick={onSpin}
              className="rounded-full bg-yellow-400 px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-black shadow-[0_0_30px_rgba(250,204,21,0.5)] transition hover:brightness-110"
            >
              Spin
            </button>
          </div>
        </div>
      )}

      {(stage === "spinning" || stage === "result") && pendingOption && (
        <div className="animate-drop-in flex flex-col items-center gap-6">
          <h2 className="font-black uppercase tracking-[0.3em] text-yellow-400 text-[clamp(1.1rem,2.5vw,1.875rem)] [text-shadow:0_0_30px_rgba(250,204,21,0.6)]">
            Double Or Nothing
          </h2>
          <SpinningWheel
            wheel={CROWN_WHEEL}
            spinToken={spinToken}
            resultOption={pendingOption}
            onSpinEnd={onSpinEnd}
            onTick={onTick}
            showResult={stage === "result"}
            showValue={false}
            footer={
              stage === "result" && bonusOption ? (
                <div className="animate-slide-down flex flex-col items-center gap-3">
                  <div
                    className={`font-mono text-[clamp(1.25rem,4vw,2rem)] font-bold ${
                      bonusOption.value >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {bonusOption.value >= 0 ? "+" : ""}
                    {bonusOption.value.toLocaleString()}
                  </div>
                  <button
                    onClick={onContinue}
                    className="rounded-full bg-yellow-400 px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-black transition hover:brightness-110"
                  >
                    Continue
                  </button>
                </div>
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
}
